import {
  defineComponent,
  h,
  withDirectives,
  Teleport,
  Transition,
  type PropType,
} from 'vue'

import ClickOutside from '@/components/directives/click-outside'
import ResizableMixin from '@/components/mixins/resizable'

import { findScrollTarget } from '@/helpers/scroll'
import type { MenuPosition } from '@/types/components/menu'

export default defineComponent({
  mixins: [ResizableMixin],

  props: {
    id: {
      type: String,
      default: null,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: [String, Number] as PropType<'auto' | number>,
      default: 'auto',
      validator: (value: 'auto' | number) => value === 'auto' || value > 0,
    },
    maxHeight: {
      type: [String, Number] as PropType<'auto' | number>,
      default: 300,
      validator: (value: 'auto' | number) => value === 'auto' || value > 0,
    },
    position: {
      type: String as PropType<MenuPosition>,
      default: 'right',
    },
    anchorElement: {
      type: HTMLElement as PropType<HTMLElement | null>,
      default: null,
    },
    include: {
      type: Array as PropType<HTMLElement[]>,
      default: () => [],
    },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      showing: false,
      scrollTarget: null as Element | (Window & typeof globalThis) | null,
      offset: 4,
    }
  },

  watch: {
    modelValue(val) {
      if (val !== this.showing) {
        if (val === true) {
          this.show()
        } else {
          this.hide()
        }
      }
    },
  },

  beforeMount() {
    this.observeElementResize = document.body
  },

  methods: {
    show() {
      this.showing = true
      this.$nextTick(() => {
        this._addScrollListener()
        this._setDimensions()
      })
    },
    hide() {
      if (!this.showing) {
        return
      }
      this._removeScrollListener()
      this.showing = false
    },
    onResize() {
      if (this.showing) {
        this._setDimensions()
      }
    },
    async _setDimensions() {
      if (!this.$refs.element || !this.$refs.content) {
        return
      }

      // Get references to elements
      const anchorElm =
        this.anchorElement || ((this.$parent?.$el || this.$el) as HTMLElement)
      const elm = this.$refs.element as HTMLElement
      const contentElm = this.$refs.content as HTMLElement
      const style = elm.style

      // Get anchor position relative to viewport
      const { top: anchorTop, left: anchorLeft } =
        anchorElm.getBoundingClientRect()

      // Calculate width constraints
      const minWidth =
        this.maxWidth === 'auto'
          ? anchorElm.offsetWidth
          : Math.min(this.maxWidth, anchorElm.offsetWidth)
      const maxWidth =
        this.maxWidth === 'auto'
          ? anchorLeft + anchorElm.offsetWidth - this.offset
          : this.maxWidth
      style.minWidth = `${minWidth}px`
      style.maxWidth = `${maxWidth}px`

      // Wait for width to apply, then measure
      await this.$nextTick()
      const innerHeight = window.innerHeight
      const elmWidth = elm.offsetWidth

      // Calculate available space above and below anchor (with viewport padding)
      const spaceBelow =
        innerHeight -
        (anchorTop + anchorElm.offsetHeight + this.offset) -
        this.offset
      const spaceAbove = anchorTop - this.offset - this.offset

      // Measure actual content height
      const contentHeight = contentElm.scrollHeight

      // Decide where to show menu (prefer below if content fits)
      const showAbove = contentHeight > spaceBelow && spaceAbove > spaceBelow

      // Set max-height based on available space
      const availableHeight = showAbove ? spaceAbove : spaceBelow
      const effectiveMaxHeight =
        this.maxHeight === 'auto'
          ? availableHeight
          : Math.min(this.maxHeight, availableHeight)
      contentElm.style.maxHeight = `${Math.max(0, effectiveMaxHeight)}px`

      // Calculate final element height (capped by max-height)
      const elmHeight = Math.min(contentHeight, effectiveMaxHeight)

      // Calculate vertical position
      let targetTop: number
      if (showAbove) {
        targetTop = Math.max(this.offset, anchorTop - elmHeight - this.offset)
      } else {
        targetTop = anchorTop + anchorElm.offsetHeight + this.offset
      }

      // Calculate horizontal position based on alignment
      let targetLeft = anchorLeft
      if (this.position == 'right') {
        targetLeft -= elmWidth - anchorElm.offsetWidth
      } else if (this.position == 'center') {
        targetLeft -= (elmWidth - anchorElm.offsetWidth) / 2
      }
      if (targetLeft < 0) {
        targetLeft = 0
      }

      // Apply position
      style.top = `${targetTop}px`
      style.left = `${targetLeft}px`
    },
    _closeMenu() {
      this.$emit('update:modelValue', false)
    },
    _removeScrollListener() {
      if (this.scrollTarget !== null) {
        this.scrollTarget.removeEventListener('scroll', this._setDimensions)
        this.scrollTarget = null
      }
      document.removeEventListener('scroll', this._setDimensions)
    },
    _addScrollListener() {
      if (this.scrollTarget === null) {
        this.scrollTarget = findScrollTarget(this.$parent?.$el ?? this.$el)
        this.scrollTarget?.addEventListener('scroll', this._setDimensions, {
          passive: true,
        })
      }
      document.addEventListener('scroll', this._setDimensions, {
        passive: true,
      })
    },
  },

  render() {
    return h(
      Teleport,
      {
        to: 'body',
      },
      h(
        Transition,
        {
          name: 'fade',
        },
        () => [
          this.showing
            ? withDirectives(
                h(
                  'div',
                  {
                    ...this.$attrs,
                    id: this.id,
                    ref: 'element',
                    class:
                      'fixed z-1000 shadow-control bg-surface border border-outline rounded-md overflow-hidden',
                    onClick: this._closeMenu,
                  },
                  [
                    h(
                      'div',
                      {
                        ref: 'content',
                        class: 'overflow-y-auto',
                      },
                      this.$slots
                    ),
                  ]
                ),
                [
                  [
                    ClickOutside,
                    this.include.length
                      ? {
                          handler: this._closeMenu,
                          include: () => this.include,
                        }
                      : this._closeMenu,
                  ],
                ]
              )
            : null,
        ]
      )
    )
  },
})
